'use strict';

import appStore from './AppStore';

//==================================================================
// Functions for dealing with service call responses in fetch.
//===================================================================

/**
 * Processes a delete response. Throws 200 if the response looks successful. Otherwise, the promise is resolved and
 * processing continues.
 */
export const processDeleteResponse = (response) => {
    /*
     * This happens before the promise has resolved. There's no way to read the response body here.
     * However, since this is a DELETE we can just check the response data and determine whether the
     * request was successful. If it is the promise can be resolved and processing can stop. Otherwise,
     * processing should continue so that we can find out more about the error condition.
     */
    if (response.ok && response.status === 200) {
        // This is the only way to short-circuit the then() chain.
        throw 200;
    } else {
        if (appStore.isDevMode()) {
            // Log the response for debugging the app is in dev mode.
            console.log(`Response [${response.status}][${response.statusText}].`);
        }
    }
    // If the response status was 401, it probably means the session has timed out.
    // We should throw 401 to have ExceptionUtils tell AppStore to trigger a login.
    if (response.status === 401) {
        throw 401;
    }

    // If the response status was 409, the api is telling us there is a new version of the UI available
    if (response.status === 409) {
        throw 409;
    }

    return response;
};

/**
 * Process a DELETE response body.
 */
export const processDeleteResponseBody = (responseBody) => {
    /*
     * Errors from our controllers are in JSON format. Server errors are usually just text (e.g. HTML).
     */
    let body;
    try {
        body = JSON.parse(responseBody)
    } catch (e) {
        throw responseBody;
    }

    // Handle redirect responses. The value of 'redirectUrl' can be empty.
    if(body.hasOwnProperty("redirectUrl")){
        _handleRedirect(body);
    }

    // Otherwise, throw the body.
    throw body;
};

/**
 * Process a post response. This doesn't do much beyond logging.
 * @param response
 */
export const processPostResponse = (response) => {
    if (! response.ok && appStore.isDevMode()) {
        // Output some debugging info on errors.
        console.log(`Response [${response.status}][${response.statusText}].`);
    }
    // If the response status was 401, it probably means the session has timed out.
    // We should throw 401 to have ExceptionUtils tell AppStore to trigger a login.
    if (response.status === 401) {
        throw 401;
    }

    // If the response status was 409, the api is telling us there is a new version of the UI available
    if (response.status === 409) {
        throw 409;
    }

    // If the response status was 503, the api is telling us we're in maintenance mode
    if (response.status === 503) {
        throw 503;
    }

    return response;
};

/**
 * Process a POST response body. Checks for error and redirect responses and throws accordingly.
 */
export const processPostResponseBody = (responseBody) => {
    /*
     * Errors from our controllers are in JSON format. Server errors are usually just text (e.g. HTML).
     */
    let body;
    try {
        body = JSON.parse(responseBody)
    } catch (e) {
        throw responseBody;
    }

    // Handle error object from the API gateway.
    if (body.hasOwnProperty("error")) {
        throw body;
    }

    // Look for an error response from our controllers.
    if (body.exceptionCode) {
        throw body;
    }

    // Otherwise, just return the body.
    return body;
};

/**
 * These are the same past POST for now.
 */
export const processPutResponse = processPostResponse;
export const processPutResponseBody = processPostResponseBody;

/**
 * Same as POST for now.
 */
export const processGetResponse = processPostResponse;
export const processGetResponseBody = processPostResponseBody;

/**
 * These are the same as POST for now.
 */
export const processPatchResponse = processPostResponse;
export const processPatchResponseBody = processPostResponseBody;